import React, { useState } from "react";
import { Mail, Lock, EyeOff, Eye } from "lucide-react";
import Loader from "./Loader";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { logIn } from "../ApiHelper/url_Helpers";
import { RiseLoader } from "react-spinners";
import "./loader.css";
import { toasterError } from "./toaster";

export const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const saveAuth = (resObj, token) => {
    try {
      const payload = JSON.stringify(resObj || {});
      const userId = resObj?.user?.id;
      if (token) {
        // Tab-specific storage (prevents session collision between tabs)
        sessionStorage.setItem('userToken', token);
        sessionStorage.setItem("isLoggedIn", "true");
        sessionStorage.setItem("hrmsUser", "true");
        if(userId) sessionStorage.setItem("userId", userId);

        // Persistent storage keyed by User ID
        if(userId) {
          localStorage.setItem(`userToken_${userId}`, token);
          localStorage.setItem(`isLoggedIn_${userId}`, "true");
          localStorage.setItem(`hrmsUser_${userId}`, "true");
          localStorage.setItem("lastUserId", userId);
        }

        // Global fallback for existing components
        localStorage.setItem('userToken', token);
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("hrmsUser", "true");
        sessionStorage.setItem('userToken', token);
      }
      localStorage.setItem('authResponse', payload);
      sessionStorage.setItem('authResponse', payload);
      if(userId) localStorage.setItem(`authResponse_${userId}`, payload);
    } catch (err) {
      console.warn('Failed to save auth data', err);
    }
    // Notify listeners (e.g., Firebase init) that auth state changed
    try {
      window.dispatchEvent(new Event('authUpdated'));
    } catch (e) {
      // ignore
    }
  };

  const handleSignIn = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await logIn({ username, password });
      const token = response.access || response.token;
      saveAuth(response, token);
      navigate("/dashboard");
      setError("");
    } catch (err) {
      console.log(err?.message);
      const errorMessage = err?.message || "Invalid Credentials";
      toasterError(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col md:flex-row items-center justify-center bg-white p-4 md:gap-x-14">
      <div className="w-full md:w-1/2 flex justify-center md:justify-end items-center mb-10 md:mb-0 md:pr-4">
        <div className="relative max-w-[500px]">
          <img
            src="/cuate.png"
            alt="Login Illustration"
            className="w-full h-auto"
          />
        </div>
      </div>

      <div className="w-full md:w-1/2 flex justify-center md:justify-start md:pl-4">
        <div className="w-full max-w-[450px] bg-[#f0f9ff] rounded-[32px] p-5 md:p-8 border border-gray-300">
          <div className="mb-5">
            <h1 className="text-3xl font-bold text-[#1a1a1a] mb-2">
              Welcome Back
            </h1>

            <p className="text-gray-500 text-lg">
              Sign in to access your dashboard
            </p>
          </div>

          <form onSubmit={handleSignIn}>
            {error && (
              <p className="text-red-500 text-sm mb-4 bg-red-50 p-2 rounded-lg">
                {error}
              </p>
            )}

            <div className="relative group mb-6">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500" />
              </div>

              <input
                required
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                className="block w-full pl-12 pr-4 py-3 bg-white border rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all border-gray-300"
              />
            </div>

            <div className="relative group mb-2">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500" />
              </div>

              <input
                required
                type={!showPassword ?"password" : "text"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="block w-full pl-12 pr-4 py-3 bg-white border rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all border-gray-300"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
              >
                {!showPassword ?  <Eye size={20} /> : <EyeOff size={20} /> }
              </button>
            </div>

            <div className="flex justify-end mb-6">
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-sm font-semibold text-[#1a2333] hover:underline"
              >
                Forgot Password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-[#1a2333] hover:bg-[#252f44] text-white font-semibold py-3 rounded-xl transition-all shadow-md ${loading ? "opacity-70" : "active:scale-[0.99]"}`}
            >
              {loading ? "Processing..." : "Sign In"}
            </button>
          </form>
          {loading &&
            <div className="loader-col">
              <RiseLoader color="#35e6e9" />
            </div>
          }
        </div>
      </div>
    </div>
  );
};
